import React from 'react';
import { ExternalLink } from 'lucide-react';

const xrplIssuers = [
  {
    domain: 'stocksprotocol.com',
    issuer: 'rhJLsttyrNGtFMNvQKHwcyvCmhCRzKvuno',
    region: 'Global'
  }
];

const stellarIssuers = [
  {
    domain: 'stocksprotocol.com',
    issuer: 'GCPPGZZAOMW3SLMXSXXLN6CKYCVS725CPY6JHUBDEJHW7KP4EJNDX6P2',
    region: 'Global'
  },
  {
    domain: 'us.stocksprotocol.com',
    issuer: 'GAD6LFRR4XFBEEJOBLX37MO3F6225NJ34PQ6RFEZY5BQB34L3M7VJ26L',
    region: 'United States'
  },
  {
    domain: 'uk.stocksprotocol.com',
    issuer: 'GDDUP6B6GOQXWYCGZDRH5P5WHHO23T37N7KJKF2CUQFZ7IP6M6D3JT32',
    region: 'United Kingdom'
  },
  {
    domain: 'eu.stocksprotocol.com',
    issuer: 'GBSIIY6LBXIA5ZVBC3B3PFCK5O2V5BXXINCJBBPZQJ6UTNVOKFVR276M',
    region: 'Europe'
  },
  {
    domain: 'hk.stocksprotocol.com',
    issuer: 'GC5LHK4CXVLQJPXOP4TQOBV66YT633SC2YPCQAMZDUKZWGKQ5OBUGITF',
    region: 'Hong Kong'
  },
  {
    domain: 'jp.stocksprotocol.com',
    issuer: 'GCVMRRDFS4DDOBCN6QGFUSWCPDHWTWJZZ6Z6DHHZC5QAQMPVNFOSIP6T',
    region: 'Japan'
  },
  {
    domain: 'ph.stocksprotocol.com',
    issuer: 'GBIDUWPIJERGXE5HYRLGKN2NHME56BBJ2FLXDNXDUBJR7L2TEN56ALJA',
    region: 'Philippines'
  }
];

const Issuers = () => {
  const truncateIssuer = (issuer) => {
    return `${issuer.slice(0, 8)}...${issuer.slice(-8)}`;
  };

  return (
    <section id="issuers-section" className="py-20 md:py-24 bg-[#0b0014]">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
        
        {/* XRPL Issuers Section */}
        <div className="mb-16 md:mb-20">
          {/* Section Header */}
          <div className="text-center mb-10 md:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">
              Our XRPL Issuers
            </h2>
            <p className="text-gray-400 text-base md:text-lg max-w-2xl mx-auto">
              Verified XRP Ledger accounts issuing tokenized stocks
            </p>
          </div>

          {/* XRPL Issuers Grid */}
          <div className="max-w-lg mx-auto">
            {xrplIssuers.map((item, index) => (
              <a
                key={index}
                href={`https://livenet.xrpl.org/accounts/${item.issuer}`}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative block p-5 md:p-6 bg-white/[0.02] hover:bg-white/[0.05] border border-white/[0.06] hover:border-purple-500/40 rounded-xl transition-all duration-300"
              >
                {/* Region Badge */}
                <div className="absolute top-4 right-4">
                  <span className="px-2.5 py-1 text-xs font-medium text-purple-300 bg-purple-500/10 rounded-full">
                    {item.region}
                  </span>
                </div>

                {/* Domain */}
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-9 h-9 rounded-lg bg-purple-500/10 flex items-center justify-center overflow-hidden">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_stocks-protocol/artifacts/pv71b1di_1.%20Logo%201.png" 
                      alt="Stocks Protocol" 
                      className="w-6 h-6 object-contain"
                    />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Home Domain</p>
                    <p className="text-white font-medium text-sm md:text-base">{item.domain}</p>
                  </div>
                </div>

                {/* Issuer */}
                <div className="mb-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Issuer Address</p>
                  <p className="text-gray-300 font-mono text-xs md:text-sm break-all leading-relaxed">
                    <span className="hidden sm:inline">{item.issuer}</span>
                    <span className="sm:hidden">{truncateIssuer(item.issuer)}</span>
                  </p>
                </div>

                {/* View on Explorer Link */}
                <div className="flex items-center gap-1.5 text-purple-400 group-hover:text-purple-300 transition-colors text-sm">
                  <span>View on XRPL</span>
                  <ExternalLink className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </div>
              </a>
            ))}
          </div>
        </div>

        {/* Stellar Issuers Section */}
        <div>
          {/* Section Header */}
          <div className="text-center mb-10 md:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">
              Our Stellar Issuers
            </h2>
            <p className="text-gray-400 text-base md:text-lg max-w-2xl mx-auto">
              Verified Stellar accounts issuing tokenized stocks across multiple jurisdictions
            </p>
          </div>

          {/* Stellar Issuers Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {stellarIssuers.map((item, index) => (
              <a
                key={index}
                href={`https://stellar.expert/explorer/public/account/${item.issuer}`}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative p-5 md:p-6 bg-white/[0.02] hover:bg-white/[0.05] border border-white/[0.06] hover:border-purple-500/40 rounded-xl transition-all duration-300"
              >
                {/* Region Badge */}
                <div className="absolute top-4 right-4">
                  <span className="px-2.5 py-1 text-xs font-medium text-purple-300 bg-purple-500/10 rounded-full">
                    {item.region}
                  </span>
                </div>

                {/* Domain */}
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-9 h-9 rounded-lg bg-purple-500/10 flex items-center justify-center overflow-hidden">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_stocks-protocol/artifacts/pv71b1di_1.%20Logo%201.png" 
                      alt="Stocks Protocol" 
                      className="w-6 h-6 object-contain"
                    />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Home Domain</p>
                    <p className="text-white font-medium text-sm md:text-base">{item.domain}</p>
                  </div>
                </div>

                {/* Issuer */}
                <div className="mb-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Issuer Address</p>
                  <p className="text-gray-300 font-mono text-xs md:text-sm break-all leading-relaxed">
                    <span className="hidden sm:inline">{item.issuer}</span>
                    <span className="sm:hidden">{truncateIssuer(item.issuer)}</span>
                  </p>
                </div>

                {/* View on Explorer Link */}
                <div className="flex items-center gap-1.5 text-purple-400 group-hover:text-purple-300 transition-colors text-sm">
                  <span>View on Stellar Expert</span>
                  <ExternalLink className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </div>
              </a>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};

export default Issuers;
